/**
 * entityMentionProviders — the "@" typeahead source for Docs.
 *
 * Fetches the mentionable entity sets once per session and filters client-side.
 * That's the same shape the existing student @mention menu uses (a preloaded
 * array + client filter) and it keeps keystroke latency at zero; the sets are
 * small enough at CYV's scale that paging them would be premature.
 *
 * PERMISSION-TOLERANT BY DESIGN. `students` and `repertoire` reads require
 * hasPerm('viewStudents') for a non-admin employee, so an ungranted teacher gets
 * PERMISSION_DENIED on those. Each collection is therefore fetched independently
 * and a failure degrades that ONE entity type to empty — it must never take the
 * whole menu down, or Docs would appear broken for a teacher who simply hasn't
 * been granted student access yet.
 */

import { collection, getDocs, query, limit, where } from 'firebase/firestore';
import { db } from '../firebase';

const MAX_PER_TYPE = 400;

/* type → how to load it and how to label it. `route` is where a chip navigates;
   null means the chip renders but isn't a link (no such surface exists in v1).
   `constraints` are extra Firestore clauses applied BEFORE the limit — see the
   note on the task source for why that ordering is load-bearing. */
const SOURCES = [
	{
		type: 'student',
		collectionName: 'students',
		label: (d) =>
			d.fullName || [d.firstName, d.lastName].filter(Boolean).join(' ') || d.name || '',
		sublabel: (d) => d.instrument || d.status || '',
		route: (id) => `/portal/work/students/${id}`,
	},
	{
		type: 'project',
		collectionName: 'projects',
		label: (d) => d.title || d.name || '',
		sublabel: (d) => d.area || d.status || '',
		route: () => null, // no project detail surface yet
	},
	{
		type: 'repertoire',
		collectionName: 'repertoire',
		label: (d) => d.songTitle || d.title || d.song || '',
		sublabel: (d) => d.artist || d.composer || '',
		route: () => null, // repertoire lives inside a student profile, not standalone
	},
	{
		// Reference an EXISTING doc — the counterpart to /Sub-page, which makes a
		// new one. Same machinery, so "link to that doc I already wrote" costs
		// nothing extra.
		type: 'doc',
		collectionName: 'workDocs',
		label: (d) => d.title || '',
		sublabel: (d) => (d.type && d.type !== 'doc' ? d.type : ''),
		route: (id) => `/portal/work/docs/${id}`,
	},
	{
		type: 'task',
		collectionName: 'teacherTasks',
		label: (d) => d.title || d.text || '',
		sublabel: (d) => d.area || d.status || '',
		route: (id) => `/portal/work/tasks?task=${encodeURIComponent(id)}`,
		// Completed tasks are not mentionable (2026-08-26, with the global
		// hide-completed work). `@` is for pointing at live work; a picker whose
		// top hits are finished tasks makes the live one harder to find.
		//
		// THIS MUST BE A QUERY CONSTRAINT, NOT A CLIENT-SIDE .filter(). The limit
		// below is applied by Firestore BEFORE anything reaches us, and
		// teacherTasks is ~600 docs of which roughly 370 are completed — so
		// filtering after the fetch would let closed tasks consume the 400-doc
		// window and silently drop OPEN tasks out of the picker entirely. That
		// would be a worse bug than the one being fixed, and an invisible one.
		//
		// `in` (name what is OPEN) rather than `!= 'completed'` (name what is
		// closed), because the stored vocabulary is not as closed as
		// src/canon/taxonomy.json implies. Measured in prod 2026-08-26: 603 task
		// docs — 232 open, 370 completed, and ONE carrying the legacy value
		// `status: 'done'` from before the vocabulary settled. A `!=` filter would
		// have let that finished card into the picker as though it were live work,
		// and would do the same for any future stray value. Naming the open states
		// means anything unrecognised is treated as not-live, which is the safe
		// direction for a picker.
		//
		// (Note this is the OPPOSITE of taskModel.isCompleted, which deliberately
		// treats an unknown status as neither open nor completed so a broken row
		// cannot silently vanish from a list. Different risk: there, hiding a row
		// loses work; here, offering a dead one is just noise. The list views
		// filter on isOpen(), so they already exclude 'done' too.)
		//
		// Single-field `in` + limit needs no composite index — verified by running
		// the exact query against production.
		constraints: [where('status', 'in', ['pending', 'in_progress'])],
	},
];

const BY_TYPE = Object.fromEntries(SOURCES.map((s) => [s.type, s]));

let cache = null; // Promise<Array<entity>> — one in-flight load shared by all callers

async function loadSource(src) {
	try {
		const snap = await getDocs(
			query(
				collection(db, src.collectionName),
				...(src.constraints || []),
				limit(MAX_PER_TYPE)
			)
		);
		return snap.docs
			.map((d) => {
				const data = d.data();
				const label = (src.label(data) || '').trim();
				if (!label) return null; // unlabelled rows are unpickable noise
				return {
					id: d.id,
					type: src.type,
					label,
					sublabel: (src.sublabel(data) || '').trim(),
				};
			})
			.filter(Boolean);
	} catch (err) {
		// Almost always PERMISSION_DENIED for an ungranted teacher. Degrade this
		// one type to empty and leave the rest of the menu working.
		if (process.env.NODE_ENV !== 'production') {
			console.warn(`[entityMentions] ${src.collectionName} unavailable:`, err?.code || err);
		}
		return [];
	}
}

async function loadAll() {
	const results = await Promise.all(SOURCES.map(loadSource));
	return results.flat();
}

/** Kick off (or reuse) the one shared load. */
export function primeEntityCache() {
	if (!cache) cache = loadAll();
	return cache;
}

/** Drop the cache so a newly-created student/task shows up without a reload. */
export function invalidateEntityCache() {
	cache = null;
}

/**
 * Search across every entity type. Exact prefix matches rank above substring
 * matches so typing a student's first name surfaces them before an unrelated
 * task that happens to contain the same word.
 */
export async function searchEntities(rawQuery, { max = 8 } = {}) {
	const all = await primeEntityCache();
	const q = (rawQuery || '').toLowerCase().trim();
	if (!q) return all.slice(0, max);

	const scored = [];
	for (const item of all) {
		const label = item.label.toLowerCase();
		const at = label.indexOf(q);
		if (at === -1) continue;
		scored.push({ item, score: at === 0 ? 0 : 1, at });
	}

	scored.sort((a, b) => a.score - b.score || a.at - b.at || a.item.label.localeCompare(b.item.label));
	return scored.slice(0, max).map((s) => s.item);
}

/** Where a chip of this type/id navigates, or null if there's no surface. */
export function routeForEntity(type, id) {
	const src = BY_TYPE[type];
	if (!src || !id) return null;
	return src.route(id);
}

export const ENTITY_LABELS = {
	doc: 'Doc',
	student: 'Student',
	project: 'Project',
	repertoire: 'Song',
	task: 'Task',
};

export const ENTITY_ICONS = {
	doc: 'fa-file-lines',
	student: 'fa-user',
	project: 'fa-folder',
	repertoire: 'fa-music',
	task: 'fa-clipboard-check',
};
